import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../hooks/auth/useAuthContext";
import { useFetchModule } from "../../hooks/firestore/useFetchModule";
import { useSiteWideOpenAccess } from "../../hooks/firestore/useSiteWideOpenAccess";
import { useSiteWideSale } from "../../hooks/firestore/useSiteWideSale";
import ModuleMetadata from "./ModuleMetadata";
import UnitDetail from "../unitDetail/UnitDetail";
import QrButton from "../../components/qr/QrButton";

import styles from "./Module.module.css";

/**
 * Displays the details of a Module, including its Units and Lessons.
 * @param {function} setShowSidebar: a function to toggle the sidebar
 */
export default function ModuleDetail({ setShowSidebar }) {
  const navigate = useNavigate();
  const { user, purchased } = useAuthContext();
  const { modId, unitId: unitParam } = useParams();

  const { moduleData, error, isPending } = useFetchModule(modId);
  const { openAccess } = useSiteWideOpenAccess();
  const { saleData } = useSiteWideSale();

  // whether the module is a freebie
  const isFreebie = (moduleData && moduleData.freebie) || false;

  // whether the user is qualified for a bonus module
  const [isBonus, matchedPrefix] =
    moduleData && moduleData.bonus_for
      ? moduleData.bonus_for
          .split(",")
          .map((prefix) => prefix.trim())
          .reduce(
            (acc, prefix) => {
              if (acc[0]) return acc;
              const isMatch =
                purchased && purchased.some((id) => id.startsWith(prefix));
              return isMatch ? [true, prefix] : acc;
            },
            [false, null],
          )
      : [false, null];

  const isOwned = !!(purchased && purchased.includes(modId));
  const isOpenAccess =
    !!user &&
    openAccess.isActive &&
    openAccess.activePrefixes.includes(modId);

  // the user is allowed to access the module if one of the following is true:
  // 1. the module is a freebie
  // 2. the user has purchased the module
  // 3. the module is a bonus for the user
  // 4. an active site-wide open-access phase lists this exact module ID
  const isAccessible = isFreebie || isOwned || isBonus || isOpenAccess;
  // check if the Unit param is valid
  const unitsData = moduleData && moduleData.units ? moduleData.units : [];
  const unitLookup = unitsData.find((unit) => unit.id === unitParam);

  useEffect(() => {
    // we should wait for moduleData to be fetched before doing any redirection
    if (!unitLookup && unitParam && moduleData && moduleData.units) {
      navigate("/404");
      return;
    }
  }, [moduleData, unitParam, unitLookup, navigate]);

  return isPending ? (
    <h2>Đợi xíu nha 😙...</h2>
  ) : (
    <>
      {error && <h2>😳 Failed to fetch module: {error}</h2>}

      {/* module metadata */}
      {moduleData && <ModuleMetadata moduleData={moduleData} />}
      {isBonus && (
        <h3>
          🎉 Bạn đã mua khóa học "{matchedPrefix}" nên được xem miễn phí nội
          dung này
        </h3>
      )}
      {isOpenAccess && !isOwned && (
        <>
          <OpenAccessBanner
            modId={modId}
            until={openAccess.expiryByPrefix?.[modId] || openAccess.until}
            saleData={saleData}
          />
          {moduleData?.show_qr_codes?.hoans !== false && (
            <QrButton moduleId={modId} />
          )}
        </>
      )}
      <hr></hr>

      {/* list of buttons to choose which Unit to view */}
      <ChooseUnit unitsData={unitsData} activeUnitId={unitParam} />

      {/* show the Unit detail, i.e. Lesson contents */}
      {unitLookup && (
        <UnitDetail
          unitData={unitLookup}
          isPurchased={isAccessible}
          setShowSidebar={setShowSidebar}
          moduleData={moduleData}
        />
      )}
    </>
  );
}

function OpenAccessBanner({ modId, until, saleData }) {
  const saleActive =
    saleData &&
    saleData.site_wide_sale_enabled &&
    saleData.site_wide_sale_percent > 0 &&
    saleData.site_wide_sale_until &&
    new Date() < saleData.site_wide_sale_until;

  const fmt = (d) =>
    `${d.toLocaleTimeString()} ngày ${d.toLocaleDateString()}`;

  return (
    <div className={styles["open-access-banner"]}>
      <p>
        Video bài giảng này đã giúp bạn "unlock" được thêm kiến thức trong đề
        tài này không? 🎁 Bạn đang được xem miễn phí "{modId}"
        {until ? ` đến ${fmt(until)}` : ""}.{" "}
        {saleActive ? (
          <>
            Hãy tranh thủ mua ngay để còn học tiếp tục sau đó nhé — toàn bộ
            website đang giảm {saleData.site_wide_sale_percent}% chỉ đến{" "}
            {fmt(saleData.site_wide_sale_until)} mà thôi.
          </>
        ) : (
          <>Hãy tranh thủ mua ngay để còn học tiếp tục sau đó nhé.</>
        )}
      </p>
    </div>
  );
}

/**
 * Carousel-style clickable elements to select a Unit.
 * @param {string} activeUnitId: used to highlight the selected Unit
 */
function ChooseUnit({ unitsData, activeUnitId }) {
  const navigate = useNavigate();
  const { courseId, modId } = useParams();

  const routeActiveUnit = (target) => {
    navigate(`/course/${courseId}/${modId}/${target}`);
  };

  return (
    <div className={styles.carousel}>
      {unitsData && unitsData.length > 0 ? (
        <ul>
          {unitsData.map((unitData, index) => (
            <li
              key={index}
              onClick={() => routeActiveUnit(unitData.id, index)}
              className={activeUnitId === unitData.id ? styles.active : {}}
            >
              {unitData.name || `Unit ${index + 1}`}
            </li>
          ))}
        </ul>
      ) : (
        <h3>😳 Khóa học này trống trơn, không tìm thấy modules nào</h3>
      )}
    </div>
  );
}
